import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';

import {OPERATORS_BY_TYPE} from './ifConditions';
import definitions from './nodeDefinitions.json';

/**
 * nodeDefinitions.json lists exactly the keys editors write; the agent API builds its schemas and checks from it
 * (agent/catalog.ts) — regenerate agent/catalog.snapshot.json after a change. assertFieldValue is the frontend half.
 * Left out on purpose: `unlayerSourceData` and legacy singular keys, which both validators leave untouched.
 */

export type NodeFieldType =
    | 'string' | 'integer' | 'number' | 'boolean' | 'enum' | 'string[]' | 'enum[]' | 'date'
    | 'text' | 'html' | 'placeholders' | 'conditionGroups';

export type NodeResourceKind =
    | 'audience' | 'survey' | 'team_member' | 'form' | 'sector' | 'service' | 'guest_category' | 'app';

export type NodeCategoryName = 'Triggers' | 'Actions' | 'Logic' | 'End';

export interface NodeFieldDefinition {
    key: string;
    type: NodeFieldType;
    /** Needed for the node to be usable (readiness), not for saving it. */
    required?: boolean;
    enum?: string[];
    min?: number;
    max?: number;
    unit?: string;
    description: string;
    example?: unknown;
    /** Ids are found through the agent's `find_resources` tool. */
    resource?: NodeResourceKind;
    /** The display-snapshot field that must be written together with this id field. */
    pairedWith?: string;
    /** Accepts `{{"Label".path}}` references (and `[[key]]` placeholders on Send Email). */
    variables?: boolean;
}

export interface NodeExample {
    request: string[];
    /** A configuration patch: present key → set, `null` → clear. */
    config: Record<string, unknown>;
}

export interface NodeDefinition {
    type: NodeType;
    label: string;
    category: NodeCategoryName;
    trigger: boolean;
    fanOut: boolean;
    description: string;
    /** `IF_CONDITION`: `["true", "false"]`; every other type: `[]`. */
    branches: string[];
    guide: string;
    fields: NodeFieldDefinition[];
    /** Must deep-equal getDefaultNodeConfig(type) (nodeDefinitions.test.ts). */
    defaults: Record<string, unknown>;
    examples: NodeExample[];
}

export const nodeDefinitionList: NodeDefinition[] = definitions as unknown as NodeDefinition[];

export const nodeDefinitions: Record<NodeType, NodeDefinition> = Object.fromEntries(
    nodeDefinitionList.map(definition => [definition.type, definition])
) as Record<NodeType, NodeDefinition>;

export const getNodeDefinition = (type: NodeType): NodeDefinition | undefined => nodeDefinitions[type];

const ruleOperators = Object.values(OPERATORS_BY_TYPE).flat().map(operator => operator.value);

const assertGroups = (groups: unknown, depth = 0): void => {
    if (depth > 8 || !Array.isArray(groups)) throw new Error('The condition groups are invalid.');
    for (const group of groups) {
        if (!group || !['AND', 'OR'].includes(group.operator) || (group.rules !== undefined && !Array.isArray(group.rules))) {
            throw new Error('Each condition group needs AND/OR and a list of rules.');
        }
        for (const rule of group.rules || []) {
            if (!rule || typeof rule.field !== 'string' || !ruleOperators.includes(rule.operator)) throw new Error('The condition rule is invalid.');
        }
        if (group.groups !== undefined) assertGroups(group.groups, depth + 1);
    }
};

const assertPlaceholders = (value: unknown): void => {
    if (!Array.isArray(value) || value.some(item => !item || typeof item.key !== 'string' || !item.key.trim()
        || /[\[\]\r\n]/.test(item.key) || typeof item.value !== 'string')
        || new Set(value.map(item => item.key)).size !== value.length) {
        throw new Error('Email variables need distinct names and text mappings.');
    }
};

const assertRange = (field: NodeFieldDefinition, value: number): void => {
    const {key, min, max} = field;
    if (min !== undefined && max !== undefined && (value < min || value > max)) throw new Error(`“${key}” must be between ${min} and ${max}.`);
    if (min !== undefined && value < min) throw new Error(`“${key}” must be at least ${min}.`);
    if (max !== undefined && value > max) throw new Error(`“${key}” must be at most ${max}.`);
};

const isStringList = (value: unknown): value is string[] => Array.isArray(value) && value.every(entry => typeof entry === 'string');

/** Throws the message the agent panel shows. Callers handle `null` (a removal) before calling. */
export const assertFieldValue = (field: NodeFieldDefinition, value: unknown): void => {
    const {key} = field;
    switch (field.type) {
        case 'string':
        case 'text':
        case 'html':
            if (typeof value !== 'string') throw new Error(`“${key}” must be text.`);
            return;
        case 'integer':
            if (typeof value !== 'number' || !Number.isInteger(value)) throw new Error(`“${key}” must be a whole number.`);
            assertRange(field, value);
            return;
        case 'number':
            if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`“${key}” must be a number.`);
            assertRange(field, value);
            return;
        case 'boolean':
            if (typeof value !== 'boolean') throw new Error(`“${key}” must be true or false.`);
            return;
        case 'enum':
            if (typeof value !== 'string' || !(field.enum || []).includes(value)) {
                throw new Error(`“${key}” must be one of: ${(field.enum || []).join(', ')}.`);
            }
            return;
        case 'string[]':
            if (!isStringList(value)) throw new Error(`“${key}” must be a list of text values.`);
            return;
        case 'enum[]':
            if (!isStringList(value) || value.some(entry => !(field.enum || []).includes(entry))) {
                throw new Error(`“${key}” must be a list of: ${(field.enum || []).join(', ')}.`);
            }
            return;
        case 'date':
            if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) throw new Error(`“${key}” must be an ISO 8601 date-time.`);
            return;
        case 'placeholders':
            assertPlaceholders(value);
            return;
        case 'conditionGroups':
            assertGroups(value);
            return;
        default:
            throw new Error(`“${key}” has an unsupported field type.`);
    }
};
